import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  BaseMerchantFields,
  FieldErrorMap,
  FieldSets,
  FieldTypeGuards,
  MerchantFieldSetValueMap,
} from "client/lib/apt-merchant";

export type FieldErrorMessageMap = FieldErrorMap;

type FieldKey = keyof FieldSets.BusinessOwnerValueMap;

type BoIndex = "0" | "1" | "2" | "3" | "4";

type ServerValidationErrorOwnerKey = `bo_${BoIndex}_${FieldKey}`;

export type ServerValidationError = BaseMerchantFields &
  {
    [key in ServerValidationErrorOwnerKey]?: string;
  };

export function transformServerValidationErrors(
  serverValidationError: ServerValidationError
): FieldErrorMessageMap {
  const errors = Object.entries(serverValidationError);
  // flatten errors obj [["website", "example error message"]]
  const result: FieldErrorMessageMap = {
    cashPay: {},
    companyInformation: {},
    customerSupport: {},
    lead: {},
    locations: Array.from({ length: 1 }, () => ({})),
    owners: Array.from({ length: 5 }, () => ({})),
    primaryBank: {},
    secondaryBank: {},
    storeDetails: {},
  };

  // initialize owners error list
  for (const [fieldProperty, errorMsg] of errors) {
    if (FieldTypeGuards.isCashPayProperty(fieldProperty) && result.cashPay) {
      result.cashPay[
        fieldProperty as keyof MerchantFieldSetValueMap["cashPay"]
      ] = { error: errorMsg + "" };
    }
    if (
      FieldTypeGuards.isCompanyInformationProperty(fieldProperty) &&
      result.companyInformation
    ) {
      result.companyInformation[
        fieldProperty as keyof MerchantFieldSetValueMap["companyInformation"]
      ] = { error: errorMsg + "" };
    }
    if (
      FieldTypeGuards.isCustomerSupportProperty(fieldProperty) &&
      result.customerSupport
    ) {
      result.customerSupport[
        fieldProperty as keyof MerchantFieldSetValueMap["customerSupport"]
      ] = { error: errorMsg + "" };
    }
    if (FieldTypeGuards.isLeadProperty(fieldProperty) && result.lead) {
      result.lead[fieldProperty as keyof MerchantFieldSetValueMap["lead"]] = {
        error: errorMsg + "",
      };
    }
    if (
      FieldTypeGuards.isPrimaryBankField(fieldProperty) &&
      result.primaryBank
    ) {
      result.primaryBank[
        fieldProperty as keyof MerchantFieldSetValueMap["primaryBank"]
      ] = { error: errorMsg + "" };
    }
    if (
      FieldTypeGuards.isSecondaryBankProperty(fieldProperty) &&
      result.secondaryBank
    ) {
      result.secondaryBank[
        fieldProperty as keyof MerchantFieldSetValueMap["secondaryBank"]
      ] = { error: errorMsg + "" };
    }
    if (
      FieldTypeGuards.isStoreDetailsProperty(fieldProperty) &&
      result.storeDetails
    ) {
      result.storeDetails[
        fieldProperty as keyof MerchantFieldSetValueMap["storeDetails"]
      ] = { error: errorMsg + "" };
    }
    // server returns owner errors in format bo_1_ssn
    if (fieldProperty.startsWith("bo_") && result.owners) {
      const [, index, propertyName] = fieldProperty.split("_");
      result.owners[Number(index)][
        propertyName as keyof FieldSets.BusinessOwnerValueMap
      ] = { error: errorMsg + "" };
    }
  }
  return result;
}

function isServerOwnerValidationError(
  validationErrors: FieldErrorMessageMap | ServerValidationError
): validationErrors is ServerValidationError {
  // if any of the keys start with bo
  const keys = Object.keys(validationErrors);
  return (
    keys.some((key) => key.startsWith("bo_")) ||
    keys.filter((key) =>
      [
        "cashPay",
        "companyInformation",
        "customerSupport",
        "lead",
        "owners",
        "locations",
        "primaryBank",
        "secondaryBank",
        "storeDetails",
      ].includes(key)
    ).length === 0
  );
}

type SliceState = {
  error: string | null;
  fieldErrorMap: FieldErrorMessageMap;
  isOpen: boolean;
};

const initialFieldErrorMap: FieldErrorMessageMap = {
  cashPay: {},
  companyInformation: {},
  customerSupport: {},
  lead: {},
  locations: [],
  owners: Array.from({ length: 5 }, () => ({})),
  primaryBank: {},
  secondaryBank: {},
  storeDetails: {},
};

const initialState: SliceState = {
  error: null,
  fieldErrorMap: initialFieldErrorMap,
  isOpen: false,
};

export const errorSlice = createSlice({
  name: "error",
  initialState,
  reducers: {
    /**
     * I don't love this, but I can't pass fieldSet in since its not serializable
     * So for now, /giphy shrug
     *
     * What this enables is the ability to clear just a slice of the fieldErrorMap
     * as opposed to the current all-or-nothing approach
     */
    clearFieldErrorMap: (
      state,
      action?: PayloadAction<{
        index?: number;
        isLocation: boolean;
        isOwner: boolean;
        name?: string;
      }>
    ) => {
      const { index, isLocation, isOwner, name } = action?.payload || {};
      if (!name || typeof index === "undefined") {
        state.fieldErrorMap = initialFieldErrorMap;
        return;
      }
      if (isOwner) {
        if (state.fieldErrorMap.owners?.[index]) {
          state.fieldErrorMap.owners[index] = {};
        }
      } else if (isLocation) {
        if (state.fieldErrorMap.locations?.[index]) {
          state.fieldErrorMap.locations[index] = {};
        }
      } else {
        state.fieldErrorMap = {
          ...state.fieldErrorMap,
          [name as keyof FieldErrorMessageMap]: initialFieldErrorMap[
            name as keyof FieldErrorMessageMap
          ],
        };
      }
    },
    clearOwnerFieldError: (
      state,
      action: PayloadAction<{
        ownerIndex: number;
      }>
    ) => {
      if (state.fieldErrorMap?.owners?.[action.payload.ownerIndex]) {
        state.fieldErrorMap.owners[action.payload.ownerIndex] = {};
      }
    },
    clearLocationFieldErrors: (
      state,
      action: PayloadAction<{
        locationIndex: number;
      }>
    ) => {
      if (state.fieldErrorMap?.locations?.[action.payload.locationIndex]) {
        state.fieldErrorMap.locations[action.payload.locationIndex] = {};
      }
    },
    hideToastError: (state) => {
      state.error = null;
      state.isOpen = false;
    },
    setFieldErrorMap: (
      state,
      action: PayloadAction<FieldErrorMessageMap | ServerValidationError>
    ) => {
      if (isServerOwnerValidationError(action.payload)) {
        state.fieldErrorMap = transformServerValidationErrors(action.payload);
      } else {
        state.fieldErrorMap = action.payload;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload ?? "";
      state.isOpen = true;
    },
  },
});
export const errorActions = errorSlice.actions;
